export interface QueryRequest {
    categoryId?: number | null;
    manufacturerId?: number | null;
    vendorId?: number | null;
    text?: string | null;
    selected?: Record<string, number[]>;
    priceFrom?: number | null;
    priceTo?: number | null;
    sort?: string;
    page?: number;
    pageSize?: number;
}

export interface FacetValueDto {
    id: number;
    label: string;
    count: number;
    selected: boolean;
    colorRgb?: string | null;
}

export interface FacetGroupDto {
    code: string;
    title: string;
    displayOrder: number;
    multiSelect: boolean;
    // Redaktørens standardtilstand (true = åpen, false = lukket); null/utelatt = innebygd standard
    defaultOpen?: boolean | null;
    values: FacetValueDto[];
}

export interface ExternalResultDto {
    title: string;
    description?: string;
    url: string;
    imageUrl?: string;
    source?: string;
    contentType?: string;
}

export interface QueryResponse {
    total: number;
    redirectUrl?: string | null;
    // «Mente du …?» — alternativ skrivemåte av søket som gir vesentlig flere treff
    suggestedQuery?: string | null;
    productIds: number[];
    facets: FacetGroupDto[];
    minPrice: number;
    maxPrice: number;
    // Antall fasettgrupper som vises før pris-blokken (utelatt/stort tall = etter alle)
    pricePosition?: number;
    // Pris-blokken er skjult av redaktøren
    priceHidden?: boolean;
    // Redaktørens standardtilstand for pris-blokken; null/utelatt = innebygd standard (lukket)
    priceDefaultOpen?: boolean | null;
    sort: string;
    page: number;
    pageSize: number;
    externalResults?: ExternalResultDto[];
}

export interface CategorySuggestionDto {
    id: number;
    name: string;
    url: string;
    // Forfedrene, rot først, uten kategorien selv — skiller kategorier med samme navn.
    path?: string[];
}

export interface AutoCompleteProductDto {
    id: number;
    name: string;
    url: string;
    imageUrl?: string | null;
    price?: string | null;
    oldPrice?: string | null;
}

export interface PopularSearchDto {
    keyword: string;
    count: number;
}

export interface AutoCompleteResponse {
    tokens: Array<{ token: string; count: number }>;
    topProductIds: number[];
    categories?: CategorySuggestionDto[];
    products?: AutoCompleteProductDto[];
    popularSearches?: PopularSearchDto[];
    // Innholdssider (artikler/topics) som matcher søketeksten — maks 3.
    pages?: ExternalResultDto[];
}

export async function query(request: QueryRequest, signal?: AbortSignal): Promise<QueryResponse> {
    const r = await fetch('/api/bits-search/query', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
        signal
    });
    if (!r.ok) throw new Error(`BitsSearch query failed: ${r.status}`);
    return await r.json();
}

export async function autoComplete(q: string, signal?: AbortSignal): Promise<AutoCompleteResponse> {
    const r = await fetch(`/api/bits-search/autocomplete?q=${encodeURIComponent(q)}`, { signal });
    if (!r.ok) throw new Error(`BitsSearch autocomplete failed: ${r.status}`);
    return await r.json();
}

export async function cards(productIds: number[], signal?: AbortSignal): Promise<string> {
    if (productIds.length === 0) return '';
    const r = await fetch(`/api/bits-search/cards?ids=${productIds.join(',')}`, { signal });
    if (!r.ok) throw new Error(`BitsSearch cards failed: ${r.status}`);
    return await r.text();
}

// «Last flere»: paginer + rendre kort i én rundtur (ingen fasett-rekomputering).
export async function cardsPage(request: QueryRequest, signal?: AbortSignal): Promise<string> {
    const r = await fetch('/api/bits-search/cardspage', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(request),
        signal
    });
    if (!r.ok) throw new Error(`BitsSearch cardspage failed: ${r.status}`);
    return await r.text();
}

// Søkeanalyse: klikk-beacon når brukeren klikker et produktkort i søkeresultatene.
// sendBeacon overlever navigasjonen til produktsiden; fetch keepalive er fallback
// (samme mønster som atferdsbeaconet i recommendations.ts). Best effort.
export function searchClickBeacon(query: string, productId: number): void {
    if (!query || !productId) return;
    const payload = JSON.stringify({ query, productId });
    const sent = navigator.sendBeacon
        && navigator.sendBeacon('/api/bits-search/click', new Blob([payload], { type: 'application/json' }));
    if (!sent) {
        fetch('/api/bits-search/click', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: payload,
            keepalive: true,
        }).catch(() => { /* best effort */ });
    }
}
